import React, { useContext } from 'react';
import { X, HelpCircle, Kanban, Plus, GripVertical, MousePointerClick, Phone, Search, ArrowDownToLine, BarChart3, MessageSquare } from 'lucide-react';
import { LanguageContext } from '../../../../Context';


/**
 * PipelineHelp - Modal displaying bilingual usage instructions for the Pipeline page.
 * Shows EN/VI content based on current language setting.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the help modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 1.0
 * @since 2026-04-16
 */
export default function PipelineHelp({ open, onClose }) {
  const { language } = useContext(LanguageContext);
  

  if (!open) return null;

  const i = language || 0;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const sections = [
    {
      icon: Kanban,
      color: '#115e59',
      bg: '#f0fdfa',
      title: ['Overview', 'Tổng quan'],
      items: [
        ['The Pipeline is a kanban board that tracks potential customers (leads) through 5 sales stages: Lead → Add Zalo → Book Test → Test → Enrol.', 'Pipeline là bảng kanban theo dõi khách hàng tiềm năng qua 5 giai đoạn: Lead → Thêm Zalo → Đặt lịch test → Test → Nhập học.'],
        ['Each stage has its own column. Leads move left-to-right as they progress through your sales process.', 'Mỗi giai đoạn có một cột riêng. Leads di chuyển từ trái sang phải khi tiến triển qua quy trình bán hàng.'],
      ],
    },
    {
      icon: ArrowDownToLine,
      color: '#14b8a6',
      bg: '#f0fdfa',
      title: ['Next Lead (Recommended)', 'Lead Tiếp theo (Khuyên dùng)'],
      items: [
        ['This is the fastest way to get new leads. Click "Next Lead" to pull the next lead from the marketing queue and open its profile immediately.', 'Đây là cách nhanh nhất để nhận lead mới. Nhấn "Lead tiếp" để lấy lead tiếp theo từ hàng đợi marketing và mở hồ sơ ngay lập tức.'],
        ['Review the lead\'s details, then log a contact or move them to the next stage.', 'Xem thông tin lead, sau đó ghi nhận liên hệ hoặc chuyển sang giai đoạn tiếp theo.'],
        ['The badge number shows how many marketing leads are still available.', 'Số trên biểu tượng cho biết bao nhiêu lead marketing còn sẵn.'],
      ],
    },
    {
      icon: Plus,
      color: '#10b981',
      bg: '#ecfdf5',
      title: ['Adding Leads Manually', 'Thêm Leads Thủ công'],
      items: [
        ['If you already have the lead\'s details, click "New Lead" in the top bar. Phone number is required.', 'Nếu bạn đã có thông tin lead, nhấn "Lead mới" trên thanh công cụ. Số điện thoại là bắt buộc.'],
        ['If a lead with the same phone already exists, a warning will appear with a link to view the duplicate.', 'Nếu lead cùng số điện thoại đã tồn tại, hệ thống sẽ cảnh báo và hiển thị liên kết để xem lead trùng.'],
        ['You can also add children (students), select the source, set priority, and add notes.', 'Bạn cũng có thể thêm thông tin trẻ (học sinh), chọn nguồn, đặt mức ưu tiên và thêm ghi chú.'],
      ],
    },
    {
      icon: GripVertical,
      color: '#8b5cf6',
      bg: '#f5f3ff',
      title: ['Drag & Drop', 'Kéo & Thả'],
      items: [
        ['Drag a lead card by its grip handle (left side) and drop it into another stage column to move it.', 'Kéo thẻ lead bằng tay cầm bên trái và thả vào cột giai đoạn khác để di chuyển.'],
        ['The status history is tracked automatically — you can view all stage changes in the History tab.', 'Lịch sử trạng thái được tự động ghi nhận — bạn có thể xem tất cả thay đổi trong tab Lịch sử.'],
      ],
    },
    {
      icon: MousePointerClick,
      color: '#0ea5e9',
      bg: '#f0f9ff',
      title: ['Lead Details', 'Chi tiết Lead'],
      items: [
        ['Click any lead card to open the detail modal with 4 tabs:', 'Nhấn vào thẻ lead bất kỳ để mở modal chi tiết với 4 tab:'],
        ['Info — Phone, Zalo, email, source, location, added by, found by, notes.', 'Thông tin — SĐT, Zalo, email, nguồn, cơ sở, người thêm, người tìm thấy, ghi chú.'],
        ['Children — List of children with name, age, and level.', 'Trẻ em — Danh sách trẻ với tên, tuổi và trình độ.'],
        ['Contact Log — History of all contacts (calls, Zalo, visits, meetings).', 'Nhật ký liên hệ — Lịch sử tất cả liên lạc (gọi điện, Zalo, thăm, họp).'],
        ['History — Timeline of all stage changes.', 'Lịch sử — Dòng thời gian tất cả thay đổi giai đoạn.'],
      ],
    },
    {
      icon: MessageSquare,
      color: '#f59e0b',
      bg: '#fffbeb',
      title: ['Contact Logging', 'Ghi nhận Liên hệ'],
      items: [
        ['Open a lead profile, then click "Log Contact" to record a call, Zalo message, visit, video call, or meeting.', 'Mở hồ sơ lead, sau đó nhấn "Ghi liên hệ" để ghi nhận cuộc gọi, tin Zalo, lần thăm, gọi video hoặc buổi họp.'],
        ['Each contact log includes the method, outcome, date, and optional notes.', 'Mỗi bản ghi liên hệ bao gồm phương thức, kết quả, ngày và ghi chú tùy chọn.'],
      ],
    },
    {
      icon: Search,
      color: '#6366f1',
      bg: '#eef2ff',
      title: ['Search', 'Tìm kiếm'],
      items: [
        ['Use the search bar to find leads by phone number or name in real-time.', 'Sử dụng thanh tìm kiếm để tìm lead theo số điện thoại hoặc tên theo thời gian thực.'],
      ],
    },
    {
      icon: BarChart3,
      color: '#64748b',
      bg: '#f8fafc',
      title: ['Stats', 'Thống kê'],
      items: [
        ['Click the chart icon to toggle the stats bar showing how many leads are in each stage.', 'Nhấn biểu đồ để bật/tắt thanh thống kê hiển thị số lượng lead ở mỗi giai đoạn.'],
        ['The header badge shows total leads and enrol conversion rate.', 'Biểu tượng tiêu đề hiển thị tổng số lead và tỷ lệ chuyển đổi nhập học.'],
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
              <HelpCircle size={16} className="text-teal-800" />
            </div>
            <h2 className="text-lg font-bold text-slate-800">
              {['Pipeline Help', 'Hướng dẫn Pipeline'][i]}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            {sections.map((section, sIdx) => {
              const SectionIcon = section.icon;
              return (
                <div key={sIdx}>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${section.bgClass || ''}`}
                      style={!section.bgClass && section.bg ? { backgroundColor: section.bg } : undefined}
                    >
                      <SectionIcon size={14} className={section.colorClass || ''} style={!section.colorClass && section.color ? { color: section.color } : undefined} />
                    </div>
                    <h3 className="text-sm font-bold text-slate-800">{section.title[i]}</h3>
                  </div>
                  <ul className="space-y-1.5 ml-[38px]">
                    {section.items.map((item, iIdx) => (
                      <li key={iIdx} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0 mt-[7px]" />
                        <span>{item[i]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 cursor-pointer"
          >
            {['Got it', 'Đã hiểu'][i]}
          </button>
        </div>
      </div>
    </div>
  );
}
